<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import LocationPicker from '@/components/location/LocationPicker.vue'
import AddressForm from '@/components/location/AddressForm.vue'
import SavedAddressScreen from '@/components/location/SavedAddressScreen.vue'
import type { SavedAddress } from '@/composables/useAddresses'
import { ORDER_TYPE, type OrderType } from '@/composables/useDelivery'
import type { LngLat } from '@/composables/useLocation'
import type { LocationChoice } from '@/composables/useLocationConfirm'
import { useLocationConfirm } from '@/composables/useLocationConfirm'
import { useScrollLock } from '@/composables/useScrollLock'
import { useLocationStore } from '@/stores/location'
import { FLOW, track } from '@/utils/track'

const props = withDefaults(defineProps<Props>(), { withSaved: false })

const isVisible = defineModel<boolean>('visible', { default: false })

const { t } = useI18n()

/**
 * Выбор точки получения. В макете это полноэкранный экран, а не модалка —
 * и это же спасает карту: внутри PrimeVue Drawer она рисует тайлы на половину
 * ширины, потому что тот навешивает на контейнер transform.
 *
 * У доставки экран начинается со списка сохранённых адресов, если они есть:
 * чаще всего клиент заказывает туда же, куда и в прошлый раз, и гонять его
 * через карту ради этого незачем. Карта открывается по «Добавить адрес» —
 * и сразу, когда сохранять ещё нечего.
 */
interface Props {
  orderType: OrderType
  /**
   * Начинать со списка сохранённых. Открывает его только тап по самому
   * адресу: за сегментом «Доставка» клиент идёт менять способ получения,
   * и ему нужна карта, а не перебор адресов.
   */
  withSaved?: boolean
}

const locationStore = useLocationStore()

const { isSaving, pendingAddress, confirmLocation, saveAddress, dropPending } = useLocationConfirm()

useScrollLock(isVisible)

/** Адрес, который правим: с ним форма сохраняется в ту же запись, без дубля. */
const editing = ref<SavedAddress | null>(null)

/** Точка, подтверждённая при правке существующего адреса. */
const edited = ref<{ coords: LngLat; name: string; house: string } | null>(null)

/** Точка, с которой открывать карту при возврате из формы. */
const picked = ref<LngLat | null>(null)

/** Список открыт — карту показываем только по явному «Добавить адрес». */
const isListOpen = ref(false)

const saved = computed(() => locationStore.addresses)

/** Есть что выбирать — только у доставки: самовывоз ведут филиалы, не адреса. */
const hasList = computed(
  () => props.withSaved && props.orderType === ORDER_TYPE.delivery && saved.value.length > 0,
)

const center = computed<LngLat | undefined>(() => {
  if (picked.value) return picked.value

  return editing.value ? [editing.value.longitude, editing.value.latitude] : undefined
})

/** Что показывает форма: правку существующего адреса или новую точку. */
const form = computed(() => edited.value ?? pendingAddress.value)

/** В форму отдаём только её поля — координаты и id она не редактирует. */
const initial = computed<Record<string, string>>(() => ({
  label: editing.value?.label ?? '',
  /* Дом берём свежий — точку только что подтвердили, а сохранённый мог устареть */
  house: form.value?.house || editing.value?.house || '',
  entrance: editing.value?.entrance ?? '',
  floor: editing.value?.floor ?? '',
  flat: editing.value?.flat ?? '',
  comment: editing.value?.comment ?? '',
}))

function reset(): void {
  picked.value = null
  edited.value = null
  editing.value = null
  dropPending()
}

/* Открыли экран — решаем, с чего начать; закрыли — чистим состояние */
watch(isVisible, (open) => {
  reset()
  isListOpen.value = open && hasList.value
})

/**
 * Выбор сохранённого адреса. Это не просто запись координат: точку проверяет
 * меню, и если адрес вышел из зоны доставки, состояние откатится, а клиент
 * останется на списке и увидит ошибку.
 */
async function pickSaved(address: SavedAddress): Promise<void> {
  track(FLOW.addressPicked)
  const result = await confirmLocation({
    delivery: ORDER_TYPE.delivery,
    coords: [address.longitude, address.latitude],
    address: address.name,
    addressId: address.id,
  })
  if (!result.ok) return

  /* Адрес уже в справочнике — форму деталей второй раз не показываем */
  dropPending()
  isVisible.value = false
}

/** Карандаш у адреса: сразу форма деталей, без карты — точка уже известна. */
function editSaved(address: SavedAddress): void {
  editing.value = address
  edited.value = {
    coords: [address.longitude, address.latitude],
    name: address.name,
    house: address.house ?? '',
  }
}

/** «Добавить адрес» — отсюда начинается обычный путь через карту. */
function addNew(): void {
  reset()
  isListOpen.value = false
}

/**
 * Новую точку ведём на форму деталей — так же, как в онбординге. Если рядом
 * уже есть сохранённый адрес, спрашивать подъезд второй раз незачем: просто
 * выбираем его и закрываем экран. При правке форма нужна всегда — за ней и шли.
 */
async function confirm(choice: LocationChoice): Promise<void> {
  const result = await confirmLocation(choice)
  if (!result.ok) return

  if (editing.value && choice.coords) {
    dropPending()
    edited.value = {
      coords: choice.coords,
      name: choice.address || editing.value.name,
      house: choice.house || editing.value.house || '',
    }
    return
  }

  if (!result.isNewAddress) isVisible.value = false
}

/**
 * Из формы обратно на карту — по тапу на адрес или на превью. Форму убираем,
 * обнулив её источник, а точку запоминаем: карта должна открыться там, где
 * клиент её поставил, а не улететь к центру региона.
 */
function editPoint(): void {
  picked.value = form.value?.coords ?? null
  edited.value = null
  dropPending()
  /* Список держал экран у себя: без этого форма возвращалась в него, а не на карту */
  isListOpen.value = false
}

/** Назад с карты: к списку, если он есть, иначе наружу. */
function leavePicker(): void {
  if (!hasList.value) {
    isVisible.value = false
    return
  }

  reset()
  isListOpen.value = true
}

/** Назад с формы правки — тоже к списку: карту клиент не открывал. */
function leaveForm(): void {
  if (editing.value && hasList.value) {
    reset()
    isListOpen.value = true
    return
  }

  editPoint()
}

/* Экран закрываем только после ответа бэкенда: не сохранилось — форма на месте */
async function saveDetails(details: Record<string, string>): Promise<void> {
  const target = editing.value
  const point = edited.value

  if (target && point) {
    const ok = await locationStore.updateAddress(target.id, {
      ...details,
      name: point.name,
      ...(point.house ? { house: point.house } : {}),
      longitude: point.coords[0],
      latitude: point.coords[1],
    })
    if (!ok) return

    edited.value = null
    track(FLOW.addressSaved, { edited: true })

    /*
     * Правили — значит сюда и едем: применяем адрес, а не просто закрываем.
     * Точку берём новую, а не из `target`: там она ещё старая, и заказ уезжал
     * с новым названием улицы и прежними координатами — в другом городе.
     */
    await pickSaved({
      ...target,
      ...details,
      name: point.name,
      ...(point.house ? { house: point.house } : {}),
      longitude: point.coords[0],
      latitude: point.coords[1],
    })
    return
  }

  if (!(await saveAddress(details))) return

  isVisible.value = false
}
</script>

<template>
  <Teleport to="body">
    <div v-if="isVisible" class="location-sheet">
      <AddressForm
        v-if="form"
        :address="form.name"
        :coords="form.coords"
        :initial="initial"
        :title="editing ? t('address.edit') : t('address.new')"
        :saving="locationStore.isAddressSaving"
        class="location-sheet__form"
        @save="saveDetails"
        @back="leaveForm"
        @edit-point="editPoint"
      />

      <SavedAddressScreen
        v-else-if="isListOpen"
        :addresses="saved"
        :saving="isSaving"
        @select="pickSaved"
        @edit="editSaved"
        @add="addNew"
        @back="isVisible = false"
      />

      <LocationPicker
        v-else
        :order-type="orderType"
        :saving="isSaving"
        :center="center"
        closable
        class="location-sheet__picker"
        @confirm="confirm"
        @back="leavePicker"
      />
    </div>
  </Teleport>
</template>

<style scoped lang="scss">
.location-sheet {
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: var(--app-max-width);
  height: var(--tg-viewport-height);
  margin: 0 auto;
  background: var(--site-bg);

  &__picker {
    flex: 1;
    min-height: 0;
  }

  /* Форма деталей длиннее экрана: прокручиваем её, а не весь оверлей */
  &__form {
    overflow: hidden auto;
    min-height: 0;
  }

}
</style>
